// Per-route document metadata.
//
// A Vite SPA serves one index.html for every route, so <title>, the
// description, the canonical link and the OG tags are all wrong on every
// route but the homepage until something updates them. This hook does that
// imperatively on navigation.
//
// The canonical link matters most. index.html ships
// <link rel="canonical" href="https://www.margin.consulting/">, so without
// this every case study would tell Google it is a duplicate of the
// homepage. Passing `path` (or a meta object from route-meta.js) rewrites
// it per route.
//
// This only reaches crawlers that execute JS. LinkedIn, Slack, iMessage and
// the classic Twitter scraper read the HTML as served and never hydrate.
// middleware.js covers those by injecting the same values at the edge. Both
// read route-meta.js so they cannot drift; do not hardcode a title here.

import { useEffect } from 'react';
import { absoluteUrl } from './route-meta.js';

export function useDocumentMeta(meta) {
  const {
    title,
    description,
    ogDescription,
    path,
    url,
    type = 'website',
    image = null,
    noindex = false,
  } = meta || {};

  const social = ogDescription || description;
  const canonical = url || (path ? absoluteUrl(path) : null);

  useEffect(() => {
    if (title) {
      document.title = title;
      setMeta('property', 'og:title', title);
      setMeta('name', 'twitter:title', title);
    }
    if (description) setMeta('name', 'description', description);
    if (social) {
      setMeta('property', 'og:description', social);
      setMeta('name', 'twitter:description', social);
    }
    if (canonical) {
      setLink('canonical', canonical);
      setMeta('property', 'og:url', canonical);
    }

    setMeta('property', 'og:type', type);

    // A large card with no image renders as an empty box, so the card type
    // follows the image rather than being declared independently.
    if (image) {
      const src = absoluteUrl(image);
      setMeta('property', 'og:image', src);
      setMeta('name', 'twitter:image', src);
      setMeta('name', 'twitter:card', 'summary_large_image');
    } else {
      removeMeta('property', 'og:image');
      removeMeta('name', 'twitter:image');
      setMeta('name', 'twitter:card', 'summary');
    }

    // Removed rather than set to "index" so a noindex route (the private
    // diagnosis pages) cannot leak its robots tag into the next route.
    if (noindex) setMeta('name', 'robots', 'noindex, nofollow');
    else removeMeta('name', 'robots');
  }, [title, description, social, canonical, type, image, noindex]);
}

function setMeta(attrName, attrValue, content) {
  let el = document.querySelector('meta[' + attrName + '="' + attrValue + '"]');
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attrName, attrValue) {
  const el = document.querySelector('meta[' + attrName + '="' + attrValue + '"]');
  if (el) el.remove();
}

function setLink(rel, href) {
  let el = document.querySelector('link[rel="' + rel + '"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
